import { PostalCodeField } from 'components/PostalCodeField'
import { StateProvinceSelect } from 'components/ProvinceSelect'
import React, { ReactElement, useState } from 'react'
import { AvForm, AvField } from 'availity-reactstrap-validation'
import { Button, Fade, UncontrolledAlert } from 'reactstrap'
import { Address, Client, Owner, OwnerOwnerCategoryEnum } from 'api'
import { OnboardingPageProps } from '../../onboardingManager'
import { FormServerErrors } from 'components/FormServerErrors'
import { t } from 'i18next'
import { addOwnerToParent, getTotalSharePercentageClient, getTotalSharePercentageOwner } from '../uboManagerHelper'
import { OwnerTypeSelect } from './SelectOwnerType'
import DeleteShareholderButton from '../DeleteShareholderButton'

export interface IndividualShareholder {
  share: number
  name: string
  firstName: string
  lastName: string
  dateOfBirth: string
  ownerCategory: OwnerOwnerCategoryEnum
  addressesAttributes: {
    address1: string
    city: string
    provinceCode: string
    postalCode: string
  }
}

interface IndividualShareholderFormProps extends OnboardingPageProps {
  onSuccess: () => void
  onCancel: () => void
  editOwner?: Owner | null
  onDelete: (owner: Owner) => void
}

const defaultAddress: Address = {
  address1: '',
  city: '',
  provinceCode: '',
  postalCode: '',
}

export function ShareholderForm({
  onNext,
  handleFieldChange,
  serverErrors,
  setServerErrors,
  validate,
  setSteps,
  activeClient,
  formRef,
  editOwner,
  parentOwner,
  onSuccess,
  onCancel,
  onDelete,
}: IndividualShareholderFormProps): ReactElement {
  const newOwnerDefaults: Owner = {
    share: 0,
    name: '',
    firstName: '',
    lastName: '',
    ownerCategory: OwnerOwnerCategoryEnum.Individual,
    dateOfBirth: '2000-01-01',
    addressesAttributes: [defaultAddress],
  }
  const [success, setSuccess] = useState(false)
  const [ownerCategory, setOwnerCategory] = useState(editOwner?.ownerCategory || OwnerOwnerCategoryEnum.Individual)

  const handleOwnerCategoryChange = (e: any) => {
    setOwnerCategory(e.target.value)
    handleFieldChange(e)
  }

  const handleValidSubmit = (event, values) => {
    onNext({
      e: event,
      ownersValues: [
        ...addOwnerToParent(activeClient.ownersAttributes ?? [], parentOwner?.id ?? null, {
          ...values,
        }),
      ],
    })?.then((response) => {
      if (response?.body?.status !== 200) {
        setSuccess(false)
        return
      } else {
        setTimeout(() => setSuccess(false), 5000)
        onSuccess()
        setSuccess(true)
      }
    })
  }

  const validateCombinedShares = (value: number, client: Client, parentOwner: Owner | null | undefined) => {
    if (parentOwner && parentOwner.ownerCategory === OwnerOwnerCategoryEnum.Entity && parentOwner.id) {
      const totalShare = getTotalSharePercentageOwner(activeClient, parentOwner.id, editOwner?.id ?? null)
      if (totalShare + value > 100) {
        return `Total shares cannot exceed 100% (${100 - totalShare}% remaining)`
      }
      return true
    }

    const totalShare = getTotalSharePercentageClient(client, editOwner?.id ?? null)
    if (totalShare + value > 100) {
      return `Total shares cannot exceed 100% (${100 - totalShare}% remaining)`
    }
    return true
  }

  return (
    <>
      <Fade in={success} mountOnEnter={true} unmountOnExit={true}>
        <UncontrolledAlert>Account Update Successful</UncontrolledAlert>
      </Fade>
      <FormServerErrors errors={(serverErrors && serverErrors['base']) || []} />
      <AvForm
        formRef={formRef}
        onValidSubmit={(event, values) => handleValidSubmit(event, values)}
        model={editOwner ? { ...editOwner } : newOwnerDefaults}
      >
        <div className="d-flex justify-content-between align-items-start">
          <h3>{`Shareholder Information for ${parentOwner?.legalBusinessName ?? activeClient?.operatingAs}`}</h3>
          {editOwner && <DeleteShareholderButton onDelete={onDelete} owner={editOwner} />}
        </div>

        {editOwner && (
          <>
            <AvField hidden name="id" type="text" required value={editOwner.id} onChange={handleFieldChange} />
          </>
        )}
        {editOwner && editOwner.ownerCategory !== OwnerOwnerCategoryEnum.Entity && (
          <AvField
            hidden
            name="addressesAttributes[0].id"
            type="text"
            required
            value={editOwner.addressesAttributes?.[0].id}
            onChange={handleFieldChange}
          />
        )}
        <OwnerTypeSelect
          name="ownerCategory"
          required
          disabled={!!editOwner}
          serverErrors={serverErrors}
          onChange={handleOwnerCategoryChange}
          isControlPerson={editOwner?.ownerCategory === OwnerOwnerCategoryEnum.ControlPerson}
        />

        <AvField
          name="share"
          type="number"
          required
          max={100}
          min={0}
          label={`Ownership % of ${parentOwner?.legalBusinessName ?? activeClient?.operatingAs}`}
          onChange={handleFieldChange}
          validate={{
            async: validate,
            required: {
              value: true,
              errorMessage: t('required_field'),
            },
            custom: (value) => validateCombinedShares(Number(value), activeClient, parentOwner),
          }}
        />

        {ownerCategory !== OwnerOwnerCategoryEnum.Entity && (
          <>
            <AvField
              name="firstName"
              type="text"
              label="First Name"
              className="mb-2"
              required
              onChange={handleFieldChange}
              validate={{
                async: validate,
                required: {
                  value: true,
                  errorMessage: t('required_field'),
                },
              }}
            />
            <AvField
              name="lastName"
              type="text"
              label="Last Name"
              className="mb-2"
              required
              onChange={handleFieldChange}
              validate={{
                async: validate,
                required: {
                  value: true,
                  errorMessage: t('required_field'),
                },
              }}
            />
            <AvField
              name="dateOfBirth"
              type="date"
              label="Date of Birth"
              className="mb-2"
              onChange={handleFieldChange}
              required
              validate={{
                async: validate,
                required: {
                  value: true,
                  errorMessage: t('required_field'),
                },
                dateRange: {
                  start: { value: -200, units: 'years' },
                  end: { value: 0, units: 'days' },
                  errorMessage: 'Date of birth must in the past',
                },
              }}
            />
            <AvField
              name="addressesAttributes[0].address1"
              type="text"
              label="Personal Street Address"
              placeholder="Ex: 123 First Street"
              className="mb-2"
              required
              onChange={handleFieldChange}
              validate={{
                async: validate,
                required: {
                  value: true,
                  errorMessage: t('required_field'),
                },
              }}
            />
            <AvField
              name="addressesAttributes[0].city"
              type="text"
              label="Town or City"
              placeholder="Enter the town or city"
              className="mb-2"
              required
              onChange={handleFieldChange}
              validate={{
                async: validate,
                required: {
                  value: true,
                  errorMessage: t('required_field'),
                },
              }}
            />
            <StateProvinceSelect
              required
              name="addressesAttributes[0].provinceCode"
              onChange={handleFieldChange}
              countryCode={activeClient?.countryCode || 'CA'}
              placeholder="Choose one..."
            />
            <PostalCodeField
              required
              name="addressesAttributes[0].postalCode"
              countryCode={activeClient?.countryCode || 'CA'}
              onChange={handleFieldChange}
              examplePlaceholder={true}
            />
          </>
        )}

        {ownerCategory === OwnerOwnerCategoryEnum.Entity && (
          <>
            <AvField
              name="legalBusinessName"
              type="text"
              label="Legal Business Name"
              className="mb-2"
              required
              onChange={handleFieldChange}
              validate={{
                async: validate,
                required: {
                  value: true,
                  errorMessage: t('required_field'),
                },
              }}
            />
            <AvField
              name="countryOfIncorporation"
              type="text"
              label="Country of Incorporation"
              className="mb-2"
              required
              onChange={handleFieldChange}
              validate={{
                async: validate,
                required: {
                  value: true,
                  errorMessage: t('required_field'),
                },
              }}
            />
            <AvField
              name="registrationNumber"
              type="text"
              label="Registration Number (Optional)"
              className="mb-2"
              onChange={handleFieldChange}
            />
          </>
        )}
        <div className="d-flex justify-content-between mt-4">
          <Button type="button" color="primary" className="btn-rotessa-secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" color="primary" className="btn-rotessa-primary">
            Submit
          </Button>
        </div>
      </AvForm>
    </>
  )
}
